import { DynamicFeed } from '@mui/icons-material'
import { Button, Dialog, Divider, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useNotification } from '../../../store/redux/hooks'
import { getCurrentUser } from '../../../store/redux/reducers/currentUserSlice'
import { useEditScheduleMutation, EditScheduleRequest } from '../../../store/redux/reducers/scheduleDateSlice'
import DateFormat from '../../../store/types/enums/DateFormat'
import DefaultHours from '../../../store/types/enums/DefaultHours'
import { SquareButton } from '../../../styles/ButtonStyles'
import { StyledContainerColumnLeft, StyledContainerRowStart } from '../../../styles/ContainerStyles'
import { statusOptions } from '../../../views/ScheduleView/ScheduleView'
import { HoursInterval } from '../../../store/types'
import { getDateFromTime } from '../../../store/utils'
import TimeFrameSection from './TimeFrameSection'
import RepeatSection from './RepeatSection'
import TimeIntervalsSection from './TimeIntervalsSection'
import { getGap } from '../../../styles/stylePropsFunctions'

const ScheduleModifyManyDialog = ({ date }: { date: string }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const selectOptions = ['Everyday', 'Selected']
  const frequencyOptions = ['EVERY_DAY', 'SELECTED_DAYS']
  const currentUser = useAppSelector(state => getCurrentUser(state))
  let employeeId = currentUser?.id === undefined ? '' : currentUser?.id

  const [selectedDaysOptions, setSelectedDaysOptions] = useState<number[]>([])
  const [repeatOption, setRepeatOption] = useState<string>(selectOptions[0])
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null)
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null)
  const { displayNotification } = useNotification()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [isRequestValid, setIsRequestValid] = useState<boolean>(false)

  const [timePeriods, setTimePeriods] = useState<HoursInterval[]>([
    {
      id: '0',
      from: DefaultHours.From,
      to: DefaultHours.To,
    },
  ])
  const [editSchedule] = useEditScheduleMutation()
  const sendRequest = () => {
    if (!isRequestValid) {
      return
    }
    let requestBody: EditScheduleRequest = {
      startDate: dayjs(startDate).format(DateFormat.YearMonthDay),
      endDate: dayjs(endDate).format(DateFormat.YearMonthDay),
      hours: timePeriods.map(timePeriod => {
        return {
          from: timePeriod.from.length === 4 ? '0' + timePeriod.from : timePeriod.from,
          to: timePeriod.to.length === 4 ? '0' + timePeriod.to : timePeriod.to,
        }
      }),
      frequency: frequencyOptions[0],
      frequencyValue: {
        frequency: 1,
        selectedDays: [],
      },
    }
    if (repeatOption === 'Selected') {
      if (selectedDaysOptions.length === 0) {
        displayNotification({ message: t('page.schedule.fillSelectedDays'), type: 'error' })
        return
      }
      dayjs.locale('en')
      requestBody.frequency = frequencyOptions[1]
      requestBody.frequencyValue = {
        frequency: 1,
        selectedDays: selectedDaysOptions.map(num => dayjs().day(num).format('dddd').toUpperCase()),
      }
    }
    editSchedule({
      employee: employeeId,
      status: statusOptions[0],
      requestBody: requestBody,
      date: date,
    })
  }

  const resetModifyManyFields = () => {
    setTimePeriods([
      {
        id: '0',
        from: DefaultHours.From,
        to: DefaultHours.To,
      },
    ])
    setRepeatOption(selectOptions[0])
    setStartDate(null)
    setEndDate(null)
    setSelectedDaysOptions([])
  }

  const handleValidationCheck = useCallback(() => {
    const sortedTimePeriods = [...timePeriods].sort((a, b) =>
      dayjs(a.from, DateFormat.HourMinute).isAfter(dayjs(b.from, DateFormat.HourMinute)) ? 1 : -1
    )

    const midnight = dayjs('00:00', 'HH:mm')

    const hasOverlap = sortedTimePeriods.some((period, i) => {
      if (i === 0) return false
      let prevTo = dayjs(sortedTimePeriods[i - 1].to, DateFormat.HourMinute)
      if (prevTo.isSame(midnight, 'minute')) {
        prevTo = prevTo.add(1, 'day')
      }
      return dayjs(period.from, DateFormat.HourMinute).isBefore(dayjs(prevTo))
    })

    if (hasOverlap) return false
    if (repeatOption === 'Selected' && selectedDaysOptions.length === 0) return false

    return !(
      startDate === null ||
      startDate === undefined ||
      !dayjs(startDate).isValid() ||
      dayjs(startDate).isBefore(dayjs(), 'day') ||
      endDate === null ||
      endDate === undefined ||
      !dayjs(endDate).isValid() ||
      dayjs(endDate).isBefore(dayjs(startDate), 'day') ||
      timePeriods.some(period => {
        return (
          !dayjs(period.from, DateFormat.HourMinute).isValid() ||
          !dayjs(period.to, DateFormat.HourMinute).isValid() ||
          (dayjs(period.to).isBefore(dayjs(period.from)) && !dayjs(period.to).isSame(midnight, 'minute'))
        )
      })
    )
  }, [endDate, startDate, timePeriods, repeatOption, selectedDaysOptions.length])

  useEffect(() => {
    setIsRequestValid(handleValidationCheck())
  }, [startDate, endDate, timePeriods, handleValidationCheck])

  const [sum, setSum] = useState({
    hours: 0,
    minutes: 0,
  })

  useMemo(() => {
    const calculateSum = (timePeriods: HoursInterval[]) => {
      let minutes = 0
      let hours

      timePeriods.forEach(timePeriod => {
        let from = getDateFromTime(timePeriod.from)
        let to = getDateFromTime(timePeriod.to)

        let midnight = dayjs('00:00', 'HH:mm')
        if (to.isSame(midnight, 'minute')) {
          to = to.add(1, 'day')
        }

        minutes += to.diff(from, 'minute')
      })

      hours = Math.floor(minutes / 60)
      minutes = minutes - 60 * hours

      return {
        hours: hours,
        minutes: minutes,
      }
    }

    setSum(calculateSum(timePeriods))
  }, [timePeriods])

  const [allValid, setAllValid] = useState(true)
  const [errorCounter, setErrorCounter] = useState(0)

  const checkIfAllValid = (timePeriods: HoursInterval[]): [boolean, number] => {
    let valid = true
    let counter = 0
    timePeriods.forEach(p => {
      if (p.from === 'Invalid Date' || p.to === 'Invalid Date') {
        valid = false
        counter++
      }
    })
    return [valid, counter]
  }

  useEffect(() => {
    let [valid, counter] = checkIfAllValid(timePeriods)
    setErrorCounter(counter)
    setAllValid(valid)
  }, [timePeriods])

  const [open, setOpen] = useState(false)

  return (
    <React.Fragment>
      <Tooltip title={<Typography variant="caption">{t('components.tooltip.modifyMany')}</Typography>}>
        <span>
          <SquareButton
            aria-label={t('components.tooltip.modifyMany')}
            color="secondary"
            onClick={() => {
              resetModifyManyFields()
              setOpen(!open)
            }}
          >
            <DynamicFeed />
          </SquareButton>
        </span>
      </Tooltip>
      <Dialog
        sx={{ color: 'text.base' }}
        PaperProps={{ elevation: 2 }}
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby={'schedule-modify-many-dialog-title'}
      >
        <StyledContainerColumnLeft
          sx={{
            p: 4,
            gap: getGap(isSmallScreen),
          }}
        >
          <StyledContainerRowStart>
            <StyledContainerColumnLeft sx={{ width: { xs: '100%', sm: '75%', md: '50%' } }}>
              <Typography component={'h3'} variant="h4" color="text.highEmphasis" id={'schedule-modify-many-dialog-title'}>
                {t('page.schedule.fillOutMany')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('page.schedule.fillOutManyPopup.overlappingInfo')}
              </Typography>
            </StyledContainerColumnLeft>
          </StyledContainerRowStart>

          <TimeIntervalsSection
            isSmallScreen={isSmallScreen}
            displayNotification={displayNotification}
            setTimePeriods={setTimePeriods}
            timePeriods={timePeriods}
            errorCounter={errorCounter}
            allValid={allValid}
            sum={sum}
          />

          <Divider />

          <RepeatSection
            isSmallScreen={isSmallScreen}
            repeatOption={repeatOption}
            setRepeatOption={setRepeatOption}
            selectOptions={selectOptions}
            setSelectedDaysOptions={setSelectedDaysOptions}
          />

          <Divider />

          <TimeFrameSection
            isSmallScreen={isSmallScreen}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />

          <Stack gap={2} justifyContent="flex-end" direction="row">
            <Button color="secondary" onClick={() => setOpen(false)} sx={{ fontWeight: 'bold' }}>
              {t('components.button.leave')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="medium"
              disabled={!isRequestValid}
              onClick={() => {
                sendRequest()
                setOpen(false)
              }}
            >
              {t('components.button.apply')}
            </Button>
          </Stack>
        </StyledContainerColumnLeft>
      </Dialog>
    </React.Fragment>
  )
}

export default ScheduleModifyManyDialog
